import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardMedia from "@mui/material/CardMedia";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";

import APButton from "../../../components/buttons/APButton";
import APIcon from "../../../components/buttons/APIcon";
import AIButton from "../../../components/buttons/APOutlineButtons/AIButton";
import APCheckbox from "../../../components/form/APCheckbox";
import { APVehicleVariant } from "../../../types/Vehicles";
import { VehicleVariantSummarisedSpecs } from "./VehicleVariantSummarisedSpecs";

export type VehicleVariantCardActions = {
  bottomActions?: {
    onAIReview?: (vehicleVariant: APVehicleVariant) => Promise<void>;
    onQuote?: (vehicleVariant: APVehicleVariant) => Promise<void>;
  };
  topActions?: {
    onToggleCompare?: (vehicleVariant: APVehicleVariant) => Promise<void>;
  };
  expandActions?: {
    onVehicleSpecs?: (vehicleVariant: APVehicleVariant) => Promise<void>;
  };
  onCardClick?: (vehicleVariantId: string) => Promise<void>;
};

export type VehicleVariantCardConfig = {
  isBeingCompared: boolean;
  disableCompare?: boolean;
  disableCardLink: boolean;
  shouldPromptForAIConsent?: boolean;
  forceOpen?: boolean;
  forceClose?: boolean;
};

const VehicleVariantCard = ({
  vehicleVariant,
  actions,
  config,
}: {
  vehicleVariant: APVehicleVariant;
  actions: VehicleVariantCardActions;
  config: VehicleVariantCardConfig;
}) => {
  const [expanded, setExpanded] = useState(false);
  const imageSrc =
    vehicleVariant.primaryVehicleImage || "/photo-placeholder.png";
  const hasCEVehicleIds =
    !!vehicleVariant.cemakeId &&
    !!vehicleVariant.cemodelId &&
    !!vehicleVariant.year &&
    !!vehicleVariant.cevariantId;

  useEffect(() => {
    if (config.forceOpen) {
      setExpanded(true);
    }
  }, [config.forceOpen]);

  useEffect(() => {
    if (config.forceClose) {
      setExpanded(false);
    }
  }, [config.forceClose]);

  const renderToggleCompare = () => {
    return (
      <APCheckbox
        name="compare"
        label="Compare"
        defaultValue={false}
        value={config.isBeingCompared}
        onChange={() => actions.topActions?.onToggleCompare?.(vehicleVariant)}
        stopPropogation={true}
        disabled={config.disableCompare}
      />
    );
  };

  return (
    <Card
      sx={{
        display: "flex",
        backgroundColor: "surface.main",
        flexDirection: "column",
      }}
      variant="outlined"
    >
      <CardActionArea onClick={() => setExpanded(!expanded)}>
        <Stack
          sx={{
            p: {
              xs: 1,
              sm: 2,
            },
          }}
          spacing={1}
        >
          {actions.topActions && (
            <Box
              sx={{
                display: "flex",
                flexDirecion: "row",
                justifyContent: "space-between",

                width: "100%",
              }}
              pb={{
                xs: 0,
                sm: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {actions.topActions?.onToggleCompare && renderToggleCompare()}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <APIcon name={expanded ? "expand-less" : "expand-more"} />
              </Box>
            </Box>
          )}
          <Box sx={{ display: "flex", alignSelf: "start", maxWidth: "100%" }}>
            <Box minWidth="160px">
              <CardMedia
                component="img"
                sx={{
                  minWidth: "5rem",
                  flex: 2,
                  mr: 1,
                  ml: {
                    xs: -1,
                    sm: -2,
                  },
                  maxWidth: "10rem",
                  height: "106px",
                  objectFit: "contain",
                }}
                image={imageSrc}
                alt={`${vehicleVariant.year} ${vehicleVariant.make} ${vehicleVariant.model}`}
              />
            </Box>
            <Stack
              spacing={1}
              sx={{
                display: "flex",
                flex: 3,
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box>
                <Typography variant="titleMedium" color="onSurface.main">
                  {vehicleVariant.year} {vehicleVariant.make}{" "}
                  {vehicleVariant.model}
                </Typography>
              </Box>
              <Box>
                <Box>
                  <VehicleVariantSummarisedSpecs
                    vehicleVariant={vehicleVariant}
                    includeRows={["money", "speedometer"]}
                  />
                </Box>
              </Box>
            </Stack>
          </Box>
          <Box>
            <Collapse in={expanded}>
              <Typography
                variant="bodyLarge"
                color="onSurface.main"
                sx={{ my: 1 }}
              >
                {vehicleVariant.variant}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <VehicleVariantSummarisedSpecs
                  vehicleVariant={vehicleVariant}
                  includeRows={["car", "range"]}
                />
                <Box>
                  {!!actions.expandActions?.onVehicleSpecs && (
                    <Box>
                      <APButton
                        text="Specs"
                        color="primary"
                        variant="outlined"
                        onClick={() =>
                          actions.expandActions?.onVehicleSpecs?.(
                            vehicleVariant
                          )
                        }
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Stack>
      </CardActionArea>
      {actions.bottomActions && (
        <Box
          sx={{
            display: "flex",
            flexDirecion: "row",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            px: {
              xs: 1,
              sm: 2,
            },
          }}
        >
          <Box>
            {actions.bottomActions?.onAIReview && (
              <AIButton
                type="vehicle-review"
                onClick={() =>
                  actions.bottomActions?.onAIReview?.(vehicleVariant)
                }
                shouldPromptForAIConsent={
                  config.shouldPromptForAIConsent ?? false
                }
                textOverride="AI Review"
              />
            )}
          </Box>
          <Box>
            {hasCEVehicleIds && !!actions.bottomActions?.onQuote && (
              <Box>
                <APButton
                  text="Quote"
                  color="secondary"
                  variant="contained"
                  icon="play"
                  onClick={() =>
                    actions.bottomActions?.onQuote?.(vehicleVariant)
                  }
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default VehicleVariantCard;
